import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { BOOKS } from "@/constants/testIds";

const books = [
  {
    id: "book-01",
    title: "T.A.C.C. Work Book",
    subtitle: "Through Adversity Comes Change",
    format: "Paperback · Amazon",
    price: "On Amazon",
    amazon_url: "https://www.amazon.com/dp/B0H5D49156",
    description: "A printable workbook — 30 struggles across 12 parts. Personal Growth, Relationships, Career, Finances, Health, Resilience, Faith, and the full T.A.C.C. Toolkit.",
    cover: "https://images.unsplash.com/photo-1544716278-e513176f20b5?q=80&w=1200&auto=format&fit=crop",
    preview: "Preview. From the T.A.C.C. Work Book — Through Adversity Comes Change. By Rich Culmer. You don't go around it. You go through it. And through is where the change happens. The T.A.C.C. Promise. I will not go around the struggle. I will not numb it, blame it, or waste it. I will go through it. Part One. Personal Growth and Identity. Struggle One. Letting go of who you used to be. The question — sit with it first. What part of your past are you still holding onto that is keeping you small? Why you must go through this struggle. Your old identity cannot fit the new level. You cannot grow into who you are becoming while clinging to who you were. Where it gets you. You finally become who you were meant to be — without apology. The struggle is not the enemy of blessing. It is the delivery system. There is always a new through.",
  },
  {
    id: "book-02",
    title: "T.A.C.C. Manual — Guide to Healing",
    subtitle: "God & the Root of All Change",
    format: "Paperback · Amazon",
    price: "On Amazon",
    amazon_url: "https://www.amazon.com/dp/B0G5286848",
    description: "Part One — God and the Root of All Change. The foundational doctrine every worker needs before the modules. Five principles. Three articles. One healer.",
    cover: "https://images.unsplash.com/photo-1519682337058-a94d519337bc?q=80&w=1200&auto=format&fit=crop",
    preview: "Preview. From the T.A.C.C. Manual — Guide to Healing. Part One. God. The Root of All Change. Before the modules. Before the audit. Before the breath. There is a reckoning most of us are too busy to name. Whether you call it God, higher power, source, or the still voice underneath all the noise — every honest change begins there. You cannot heal what you do not first surrender. You cannot rebuild what you do not first hand over. This part of the manual is not doctrine. It is an invitation. Everything that follows — the five principles, the three articles, and the one healer — depends on this one decision. To stop pretending you are the source of the change. And to become the one who receives it.",
  },
];

export default function BooksSection({ heading = true }) {
  return (
    <section id="books" className="relative bg-[#FFFFFF] py-28 md:py-40 border-y border-[#E7DFD5]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {heading && (
          <div className="grid grid-cols-12 gap-y-10 mb-20">
            <div className="col-span-12 md:col-span-6">
              <div className="section-tag mb-6">§ Listen first · Books &amp; Kindle · Amazon</div>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
                The <span className="italic text-[#B45309]">reading list</span> for hard days.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-[#44403C] text-lg leading-relaxed max-w-md">
                Three volumes. One doctrine. Preview the opening pages in Rich&apos;s narrated voice, then buy on Amazon — Kindle or paperback.
              </p>
            </div>
          </div>
        )}

        <div data-testid={BOOKS.grid} className="grid grid-cols-12 gap-6 md:gap-8">
          {books.map((b, i) => (
            <motion.a
              key={b.id}
              href={b.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={BOOKS.card(b.id)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.65, 0, 0.35, 1] }}
              className={`group col-span-12 md:col-span-6 tacc-card p-6 md:p-8 flex flex-col`}
            >
              <div className={`relative overflow-hidden aspect-[4/3] bg-black mb-6`}>
                <img
                  src={b.cover}
                  alt={b.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter,transform] duration-[900ms] ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4 section-tag">{b.format}</div>
                <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full border border-[#E7DFD5] flex items-center justify-center backdrop-blur-md bg-black/40 group-hover:bg-[#F59E0B] group-hover:border-[#F59E0B] transition-colors duration-500">
                  <ArrowUpRight size={16} />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">{b.subtitle}</div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tight mb-3">{b.title}</h3>
                <p className="text-[#78716C] text-sm leading-relaxed mb-4">{b.description}</p>
                <div
                  className="mb-4"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <AudioListen
                    text={b.preview}
                    label="Preview the first pages"
                    testid={`book-listen-${b.id}`}
                    analyticsKind="book"
                    analyticsId={b.id}
                    bookmarkTitle={b.title}
                    bookmarkUrl={b.amazon_url}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-[#E7DFD5]">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#B45309]">{b.price}</span>
                <span data-testid={BOOKS.buyBtn(b.id)} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase">
                  <BookOpen size={14} />
                  Buy on Amazon
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
